import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Input, Popconfirm, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApartmentOutlined,
  ApiOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { StartRunDrawer } from '@/runs/StartRunDrawer';
import { TriggersDrawer } from '@/flows/TriggersDrawer';
import { FLOW_SUMMARIES } from '@/flow-ir/samples';
import { archiveFlow, listFlows, unarchiveFlow } from '@/api/flows';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { useAuth } from '@/auth/OlAuthContext';
import { AssignSpokeDrawer } from '@/acl/AssignSpokeDrawer';
import { stageMeta } from '@/theme/tokens';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';

const { Text } = Typography;

/** A unified row model over API flow summaries and checked-in samples. */
interface Row {
  id: string;
  name: string;
  description?: string;
  version: number;
  stages: string[];
  jurisdictions: string[];
  variant?: boolean;
  published?: number | null;
  archived?: boolean;
  nodeCount?: number;
  edgeCount?: number;
}

function StageTag({ stage }: { stage: string }) {
  const m = stageMeta(stage);
  return (
    <span
      title={m.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        padding: '1px 7px',
        borderRadius: 999,
        border: '1px solid var(--ol-border)',
        color: 'var(--ol-text-secondary)',
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: 2, background: m.accent, flex: 'none' }} />
      {m.label}
    </span>
  );
}

const sampleRows = (): Row[] =>
  FLOW_SUMMARIES.map((s) => ({
    id: s.id,
    name: s.name,
    description: s.description,
    version: s.version,
    stages: s.stages,
    jurisdictions: s.jurisdictions,
    variant: s.variant,
    nodeCount: s.nodeCount,
    edgeCount: s.edgeCount,
  }));

/** The row a Run / Triggers drawer is open for. */
interface DrawerTarget {
  kind: 'run' | 'triggers';
  flowId: string;
  name: string;
  publishedVersion: number | null;
}

export function FlowsListPage() {
  usePageTitle('Flows');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  /** `?triggers` (from the Runs empty state) shows the wiring hint; `?triggers=<flowId>` opens it. */
  const triggersParam = searchParams.get('triggers');
  const [drawer, setDrawer] = useState<DrawerTarget | null>(null);
  const { user } = useAuth();
  // OPSFL-FLOWOPS-1 C3: the fence affordance is hub_admin's (acl.* writes) — hidden otherwise;
  // the server re-checks regardless (an unhidden affordance still 403s).
  const canFence = user?.roles?.includes('hub_admin') ?? false;
  const [fenceTarget, setFenceTarget] = useState<Row | null>(null);

  const [rows, setRows] = useState<Row[]>(() => sampleRows());
  const [usingSamples, setUsingSamples] = useState(true);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'active' | 'archived'>('active');
  const [archiveNote, setArchiveNote] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    let cancel = false;
    setLoading(true);
    setLoadError(null);
    listFlows(view === 'archived')
      .then((flows) => {
        if (cancel) return;
        setRows(
          flows
            .filter((f) => (view === 'archived' ? f.archived : !f.archived))
            .map((f) => ({
              id: f.id,
              name: f.name,
              version: f.latestVersion,
              stages: f.stages ?? [],
              jurisdictions: f.jurisdictions ?? [],
              published: f.publishedVersion ?? null,
              archived: f.archived ?? false,
            })),
        );
        setUsingSamples(false);
      })
      .catch((e) => {
        if (cancel) return;
        // Not live yet → keep the checked-in samples so the read-only path still works.
        const err = normalizeApiError(e);
        if (!isNotAvailable(err)) {
          // A REAL failure. The samples fallback still renders, but silently showing sample data
          // where the estate should be is a lie by omission — say so, loudly, with a retry.
          console.warn('listFlows failed:', err.message);
          setLoadError(err.message);
        }
        setRows(sampleRows());
        setUsingSamples(true);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [view]);

  useEffect(() => refresh(), [refresh]);

  useEffect(() => {
    if (!triggersParam || usingSamples || loading) return;
    const r = rows.find((x) => x.id === triggersParam);
    if (r) {
      setDrawer({ kind: 'triggers', flowId: r.id, name: r.name, publishedVersion: r.published ?? null });
      // One-shot: strip the param so closing the drawer does not re-open it.
      const next = new URLSearchParams(searchParams);
      next.delete('triggers');
      setSearchParams(next, { replace: true });
    }
  }, [triggersParam, usingSamples, loading, rows, searchParams, setSearchParams]);

  const onArchive = useCallback(
    (r: Row) => {
      archiveFlow(r.id, archiveNote.trim() || undefined)
        .then(() => {
          message.success(`'${r.id}' archived — recover it any time from the Archived view.`);
          setArchiveNote('');
          refresh();
        })
        .catch((e) => message.error(normalizeApiError(e).message));
    },
    [archiveNote, message, refresh],
  );

  const onUnarchive = useCallback(
    (r: Row) => {
      unarchiveFlow(r.id)
        .then(() => {
          message.success(`'${r.id}' restored to the active estate.`);
          refresh();
        })
        .catch((e) => message.error(normalizeApiError(e).message));
    },
    [message, refresh],
  );

  const columns: ColumnsType<Row> = useMemo(
    () => [
      {
        title: 'Flow',
        dataIndex: 'name',
        key: 'name',
        render: (_, r) => (
          <div style={{ minWidth: 220 }}>
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              {r.name}
              {r.variant ? (
                <Tag color="geekblue" bordered={false} style={{ fontSize: 10 }}>
                  variant
                </Tag>
              ) : null}
              {r.published != null && (
                <Tag color="success" bordered={false} style={{ fontSize: 10 }}>
                  published v{r.published}
                </Tag>
              )}
            </div>
            {r.description && (
              <Text
                type="secondary"
                style={{ fontSize: 12, display: 'block', maxWidth: 460 }}
                ellipsis={{ tooltip: r.description }}
              >
                {r.description}
              </Text>
            )}
            <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
              {r.id}
            </Text>
          </div>
        ),
      },
      {
        title: 'Ver',
        dataIndex: 'version',
        key: 'version',
        width: 64,
        render: (v: number) => <span className="ol-mono">v{v}</span>,
      },
      {
        title: 'Lifecycle stages',
        key: 'stages',
        render: (_, r) => (
          <Space size={4} wrap>
            {r.stages.length ? r.stages.map((s) => <StageTag key={s} stage={s} />) : <Text type="secondary">—</Text>}
          </Space>
        ),
      },
      {
        title: 'Jurisdiction',
        key: 'jur',
        width: 120,
        render: (_, r) =>
          r.jurisdictions.length ? (
            r.jurisdictions.map((j) => (
              <Tag key={j} bordered={false} style={{ fontSize: 11 }}>
                {j}
              </Tag>
            ))
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: '',
        key: 'go',
        width: 190,
        align: 'right',
        render: (_, r) => (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            {view === 'active' && !usingSamples && canFence && (
              <Button
                size="small"
                type="text"
                icon={<SafetyCertificateOutlined />}
                aria-label={`Assign ${r.id} to a spoke`}
                title="Assign to spoke (access fence)"
                onClick={() => setFenceTarget(r)}
              />
            )}
            {view === 'active' && !usingSamples && (
              <>
                <Tooltip
                  title={
                    r.published != null
                      ? `Start a run of ${r.id} v${r.published}`
                      : 'Nothing published yet — runs execute the signed, published version only. Open the flow and publish it; the publish drawer ends with "Run it now".'
                  }
                >
                  <Button
                    size="small"
                    type="text"
                    icon={<PlayCircleOutlined />}
                    disabled={r.published == null}
                    aria-label={`Run ${r.id}`}
                    onClick={() =>
                      setDrawer({ kind: 'run', flowId: r.id, name: r.name, publishedVersion: r.published ?? null })
                    }
                  />
                </Tooltip>
                <Tooltip title={`Triggers — which clocks, signals and API names start ${r.id}`}>
                  <Button
                    size="small"
                    type="text"
                    icon={<ApiOutlined />}
                    aria-label={`Triggers ${r.id}`}
                    onClick={() =>
                      setDrawer({ kind: 'triggers', flowId: r.id, name: r.name, publishedVersion: r.published ?? null })
                    }
                  />
                </Tooltip>
              </>
            )}
            {view === 'active' && !usingSamples && (
              <Popconfirm
                title={`Archive '${r.id}'?`}
                description={
                  <div style={{ maxWidth: 300 }}>
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                      It leaves the catalogue and cannot start runs; nothing is deleted and you
                      can restore it from the Archived view.
                    </div>
                    <Input
                      size="small"
                      placeholder="Note (optional)"
                      value={archiveNote}
                      onChange={(e) => setArchiveNote(e.target.value)}
                    />
                  </div>
                }
                okText="Archive"
                onConfirm={() => onArchive(r)}
              >
                <Button size="small" type="text" icon={<InboxOutlined />} aria-label={`Archive ${r.id}`} />
              </Popconfirm>
            )}
            {view === 'archived' && (
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => onUnarchive(r)}
                aria-label={`Restore ${r.id}`}
              >
                Restore
              </Button>
            )}
            <RightOutlined style={{ color: 'var(--ol-text-tertiary)' }} />
          </Space>
        ),
      },
    ],
    [view, usingSamples, canFence, archiveNote, onArchive, onUnarchive],
  );

  return (
    <div className="ol-page">
      <PageHeader
        title="Flows"
        icon={<ApartmentOutlined />}
        subtitle="The flow estate. Open any flow to design it on the Workflow Designer canvas — drag-drop authoring, inline diagnostics, variant diff, and the maker-checker publish pipeline."
        extra={
          <>
            <Segmented
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Archived', value: 'archived' },
              ]}
              value={view}
              onChange={(v) => setView(v as 'active' | 'archived')}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/flows/new')}>
              New flow
            </Button>
          </>
        }
      />

      {loadError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
          message="Could not load this hub's flows — showing checked-in samples instead"
          description={loadError}
          action={
            <Button size="small" onClick={() => refresh()}>
              Retry
            </Button>
          }
        />
      )}

      {usingSamples && (
        <Alert
          type="info"
          showIcon
          banner
          style={{ marginBottom: 16, borderRadius: 8 }}
          message={
            <span>
              Showing checked-in Flow IR samples (the product API is not live yet). Opening a sample
              gives the read-only canvas; a live flow opens fully editable.
            </span>
          }
        />
      )}

      {triggersParam === '' && !usingSamples && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('triggers');
            setSearchParams(next, { replace: true });
          }}
          style={{ marginBottom: 16, borderRadius: 8 }}
          message="Wire a trigger"
          description="Pick a published flow and use its Triggers action (the plug icon) to bind a clock type, signal type or API name to it. Triggers are hub configuration — the flow itself is unchanged."
        />
      )}

      <Table<Row>
        rowKey="id"
        loading={loading && rows.length > 0}
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading flows" />
          ) : loadError ? (
            <ErrorState what="this hub's flows" detail={loadError} onRetry={() => refresh()} compact />
          ) : (
            <EmptyState
              compact
              icon={<ApartmentOutlined />}
              title={view === 'archived' ? 'Nothing archived' : 'No flows yet'}
              description={
                view === 'archived'
                  ? 'Archived flows leave the working catalogue but are never deleted — restore one at any time.'
                  : 'A flow is the workflow your operation runs: steps, decisions and capability calls, versioned and published through maker-checker. Start one from a template, or open a blank canvas with New flow.'
              }
            />
          ),
        }}
        onRow={(r) => ({
          onClick: () => navigate(`/flows/${r.id}`),
          onKeyDown: (e) => {
            if (e.key === 'Enter') navigate(`/flows/${r.id}`);
          },
          tabIndex: 0,
          style: { cursor: 'pointer' },
          'aria-label': `Open ${r.name} in the Workflow Designer`,
        })}
      />

      {/* OPSFL-FLOWOPS-1 A1/A2: the start affordance + the trigger-wiring surface, per row. */}
      <StartRunDrawer
        open={drawer?.kind === 'run'}
        flowId={drawer?.flowId ?? ''}
        flowName={drawer?.name}
        publishedVersion={drawer?.publishedVersion ?? null}
        onClose={() => setDrawer(null)}
      />
      <TriggersDrawer
        open={drawer?.kind === 'triggers'}
        flowId={drawer?.flowId ?? ''}
        flowName={drawer?.name}
        publishedVersion={drawer?.publishedVersion ?? null}
        onClose={() => setDrawer(null)}
      />
      {/* OPSFL-FLOWOPS-1 C3: the spoke fence affordance (hub_admin). */}
      <AssignSpokeDrawer
        flowId={fenceTarget?.id ?? null}
        flowName={fenceTarget?.name}
        onClose={() => setFenceTarget(null)}
      />
    </div>
  );
}
